import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import NewEventModal from '../components/NewEventModal';
import { countryName } from '../lib/countries';
import { CURRENCIES } from '../lib/currency';
import type { EventListStatus, EventSummary } from '../lib/types';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<EventListStatus>('active');
  const [events, setEvents] = useState<EventSummary[] | null>(null);
  const [archivedCount, setArchivedCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  async function refresh(forView: EventListStatus = view) {
    setEvents(null);
    try {
      const [list, archived] = await Promise.all([
        api.listEvents(forView),
        // Cheap-ish — could be optimised later by including counts in the
        // active list endpoint; for now we just call /events?status=archived
        // when on the active view to keep the badge up to date.
        forView === 'archived' ? Promise.resolve(null) : api.listEvents('archived'),
      ]);
      setEvents(list);
      if (archived !== null) setArchivedCount(archived.length);
      else setArchivedCount(list.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    }
  }

  useEffect(() => {
    refresh(view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Modal-driven create flow lives in NewEventModal. Once it returns the
  // new event, we navigate the user straight into the editor.

  async function remove(id: string) {
    if (!confirm('Delete this event? This cannot be undone — use Archive instead if you might want it back.')) return;
    try {
      await api.deleteEvent(id);
      setEvents((cur) => (cur ?? []).filter((e) => e.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  async function archive(id: string) {
    setError(null);
    try {
      await api.archiveEvent(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Archive failed');
    }
  }

  async function unarchive(id: string) {
    setError(null);
    try {
      await api.unarchiveEvent(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Restore failed');
    }
  }

  async function copy(id: string, currentName: string) {
    const suggestion = `Copy of ${currentName || 'Untitled'}`;
    const newName = window.prompt('Name for the copy:', suggestion);
    if (newName === null) return; // user cancelled
    const trimmed = newName.trim();
    if (!trimmed) {
      setError('Name cannot be empty.');
      return;
    }
    setError(null);
    try {
      await api.copyEvent(id, trimmed);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Copy failed');
    }
  }

  return (
    <div className="app">
      <div className="topbar">
        <div className="topbar-row">
          <div className="topbar-app">
            <img className="brand-logo" src="/favicon.svg" alt="" />
            <span>Revenue Estimator</span>
            <a
              className="kofi-mini"
              href="https://ko-fi.com/W4Z120VC3R"
              target="_blank"
              rel="noopener noreferrer"
              title="Buy me a coffee"
            >
              <img
                className="kofi-mini-img"
                src="https://storage.ko-fi.com/cdn/kofi4.png?v=6"
                alt="Buy me a coffee"
              />
            </a>
          </div>
          <div className="row center gap-10">
            <span className="muted">{user?.email}</span>
            <button className="btn ghost" onClick={() => { void logout(); }}>Sign out</button>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="dash-head">
          <h1 className="dash-title">Your events</h1>
          {view === 'active' && (
            <button className="btn" onClick={() => setModalOpen(true)}>
              + New event
            </button>
          )}
        </div>

        <div className="tabs">
          <button
            className={`tab ${view === 'active' ? 'active' : ''}`}
            onClick={() => setView('active')}
          >
            Active
          </button>
          <button
            className={`tab ${view === 'archived' ? 'active' : ''}`}
            onClick={() => setView('archived')}
          >
            Archived
            {archivedCount !== null && archivedCount > 0 && (
              <span className="tab-count">{archivedCount}</span>
            )}
          </button>
        </div>

        {error && <div className="error-banner mb-16">{error}</div>}

        {events === null ? (
          <div className="muted">Loading…</div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            {view === 'active' ? (
              <>
                <h2>No events yet</h2>
                <p>Create your first event to start estimating revenue and costs.</p>
                <button className="btn" onClick={() => setModalOpen(true)}>+ New event</button>
              </>
            ) : (
              <>
                <h2>Nothing archived</h2>
                <p>Archived events show up here. They're hidden from the active list but kept around so you can come back to them anytime.</p>
              </>
            )}
          </div>
        ) : (
          <div className="event-list">
            {events.map((e) => (
              <div
                key={e.id}
                className={`event-card ${e.archived_at ? 'is-archived' : ''}`}
                onClick={() => navigate(`/events/${e.id}`)}
              >
                <div className="event-card-tags">
                  <span className={`event-type-badge ${e.event_type === 'meetup' ? 'meetup' : 'conference'}`}>
                    {e.event_type === 'meetup' ? 'Meetup' : 'Conference'}
                  </span>
                  {e.archived_at && (
                    <span className="event-card-badge">Archived</span>
                  )}
                </div>
                <div className="name">{e.name || 'Untitled'}</div>
                <div className="meta">
                  {formatLocation(e.city, e.country_code) || '—'}
                  {' · '}
                  {e.start_date ?? '—'}
                  {e.end_date ? ` – ${e.end_date}` : ''}
                </div>
                <div className="meta">
                  {e.conference_days}d conf · {e.workshop_days}d workshop ·{' '}
                  {currencyName(e.currency)}
                </div>
                <div className="actions" onClick={(ev) => ev.stopPropagation()}>
                  <button
                    className="btn sm ghost"
                    onClick={() => navigate(`/events/${e.id}`)}
                  >
                    Open
                  </button>
                  <button
                    className="btn sm ghost"
                    onClick={() => copy(e.id, e.name)}
                  >
                    Copy
                  </button>
                  {e.archived_at ? (
                    <button
                      className="btn sm ghost"
                      onClick={() => unarchive(e.id)}
                    >
                      Restore
                    </button>
                  ) : (
                    <button
                      className="btn sm ghost"
                      onClick={() => archive(e.id)}
                    >
                      Archive
                    </button>
                  )}
                  <button
                    className="btn sm danger"
                    onClick={() => remove(e.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <NewEventModal
          onClose={() => setModalOpen(false)}
          onCreated={(ev) => {
            setModalOpen(false);
            navigate(`/events/${ev.id}`);
          }}
        />
      )}
    </div>
  );
}

function currencyName(code: string): string {
  const c = CURRENCIES.find((x) => x.code === code);
  return c ? `${c.code}` : code;
}

function formatLocation(city: string, countryCode: string): string {
  const parts: string[] = [];
  if (city) parts.push(city);
  if (countryCode) parts.push(countryName(countryCode));
  return parts.join(', ');
}
